import { TDisplayDefinitionEntry } from '@internal/utils/displayDefinition/entry/TDisplayDefinitionEntry';
import { TDisplayDefinition } from '@internal/utils/displayDefinition/TDisplayDefinition';
import { localeNamespace, TLocale } from '@internal/utils/machine/i18n/Locale';
import { Machine } from '@internal/utils/machine/Machine';
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute';
import { useFinancingData } from '@internal/utils/machine/useFinancingData';
import { Button } from '@renderer-ui-library/atoms';
import {
    ClickToCopyWrapper,
    LeasingQualifiedButton,
    MachineDetailsContact,
    MachineHighlights,
    TrustPilot,
} from '@renderer-ui-library/molecules';
import { Grid, Typography } from '@renderer-ui-library/mui';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { ContactWizardContext } from '@renderer-ui-library/organisms';
import { translations } from 'i18n/translations';
import { useTranslation } from 'next-i18next';
import React, { useContext, useMemo } from 'react';
import { displayDefinitionEntryToString } from 'utils/displayDefinition/entry/displayDefinitionEntryToString';
import { formatPrice } from 'utils/numbers/formatPrice';
import { Service } from './IMachineOverviewBlockData';
import styles from './machineOverviewBlock.module.scss';
import { USPs } from './USPs/USPs';

interface Props {
    machine: Machine;
    locale: TLocale;
    services: Service[];
    displayDefinition: TDisplayDefinition | null;
}

export const MachineOverviewSidebar: React.FC<Props> = React.memo(
    ({ machine, locale, displayDefinition, services }) => {
        const { t } = useTranslation(localeNamespace.common);
        const { isMobile } = useMediaQuery();

        const { openRequestInfo } = useContext(ContactWizardContext);
        const formattedPrice = machine.price
            ? formatPrice(locale, machine.price)
            : null;

        const { getFinancingData } = useFinancingData();
        const financingData = getFinancingData(machine);

        const columnsDefinition = displayDefinition?.categories[0];
        const highlightsDefinition: TDisplayDefinitionEntry[] =
            displayDefinition?.categories[1]?.entries ?? [];

        const columnLabel = useMemo(
            () => ({
                [MachineAttribute.YEAR_OF_PRODUCTION.toString()]: t(
                    translations.machineOverviewYear,
                ),
                [MachineAttribute.ENGINE_HOURS.toString()]: t(
                    translations.machineOverviewHours,
                ),
                [MachineAttribute.LOCATION_COUNTRY.toString()]: t(
                    translations.machineOverviewLocation,
                ),
                [MachineAttribute.ENGINE_HP.toString()]: t(
                    translations.machineOverviewPower,
                ),
                [MachineAttribute.ENGINE_POWER.toString()]: t(
                    translations.machineOverviewPower,
                ),
            }),
            [t],
        );

        const fourColumns = useMemo(
            () => columnsDefinition?.entries.slice(0, 4),
            [columnsDefinition?.entries],
        );

        if (!machine) {
            return null;
        }

        return (
            <>
                <div className={styles.titleWrapper}>
                    <Typography
                        variant={isMobile ? 'h2' : 'h3'}
                        component="h1"
                        title={machine.name}
                    >
                        {machine.name}
                    </Typography>
                </div>
                <div className={styles.detailSection}>
                    <div className={styles.machineIdWrapper}>
                        <div className={styles.machineId}>
                            <ClickToCopyWrapper text={machine.machineGroupId}>
                                <Typography
                                    variant="body2"
                                    color="textPrimary"
                                    component="span"
                                >
                                    <strong>ID:</strong>{' '}
                                    {`${machine.machineGroupId}`}
                                </Typography>
                            </ClickToCopyWrapper>
                        </div>
                    </div>

                    <div>
                        {formattedPrice && (
                            <div className={styles.priceWrapper}>
                                <Typography
                                    variant={isMobile ? 'h2' : 'h3'}
                                    component="h4"
                                    className={styles.price}
                                >
                                    {formattedPrice}*
                                </Typography>
                                <Typography component="p" variant="subtitle1">
                                    / {t(translations.priceTaxInfo)}
                                </Typography>
                            </div>
                        )}

                        {false && financingData && (
                            <>
                                <Typography variant="h3" component="span">
                                    /
                                </Typography>
                                <Typography variant="h4" component="span">
                                    {formatPrice(
                                        locale,
                                        // biome-ignore lint/style/noNonNullAssertion: legacy
                                        financingData!.monthlyRate,
                                    )}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    component="span"
                                    className={styles.leasingPriceMonthly}
                                >
                                    /{t(translations.leasingPriceMonthly)}
                                    <span
                                        className={styles.leasingPriceInfoIcon}
                                    >
                                        *
                                    </span>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    className={styles.vatInfo}
                                >
                                    {t(translations.priceTaxInfo)}
                                </Typography>
                            </>
                        )}

                        {!formattedPrice && (
                            <Button
                                onClick={openRequestInfo}
                                style={{ padding: 0 }}
                                variant="text"
                            >
                                <Typography
                                    variant="h4"
                                    component="span"
                                    className={styles.priceOnRequest}
                                >
                                    {t(translations.priceOnRequest)}*
                                </Typography>
                            </Button>
                        )}
                    </div>
                </div>
                <div className={styles.detailSection}>
                    {fourColumns && (
                        <Grid container columnGap={3}>
                            {fourColumns.map(entry => {
                                const text = displayDefinitionEntryToString(
                                    locale,
                                    machine,
                                    entry,
                                    ', ',
                                    true,
                                );
                                return (
                                    <Grid
                                        item
                                        xs="auto"
                                        key={entry.machineAttribute}
                                        className={styles.specsWrapper}
                                    >
                                        <Typography>
                                            {(columnLabel[
                                                entry.machineAttribute
                                            ] as string) ?? entry.label}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            noWrap
                                            className={styles.specsValue}
                                        >
                                            {text ? (
                                                <>
                                                    {entry.machineAttribute ===
                                                    MachineAttribute.ENGINE_HP
                                                        ? `${parseInt(text, 10)} ${t(
                                                              translations.enginePowerUnitHP,
                                                          )}`
                                                        : text}
                                                    {machine
                                                        .unconfirmedAttributes[
                                                        entry.machineAttribute
                                                    ] && (
                                                        <sup
                                                            style={{
                                                                marginLeft: 4,
                                                            }}
                                                        >
                                                            *
                                                        </sup>
                                                    )}
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </Typography>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </div>
                {highlightsDefinition.length > 0 && (
                    <div className={styles.detailSection}>
                        <MachineHighlights
                            definition={highlightsDefinition}
                            locale={locale}
                            machine={machine}
                        />
                    </div>
                )}
                {isMobile && (
                    <div className={styles.uspsWrapper}>
                        <USPs services={services} />
                    </div>
                )}
                <div className={styles.detailSection}>
                    <MachineDetailsContact
                        machineGroupId={machine.machineGroupId}
                        locale={locale}
                    />
                </div>
                {financingData && (
                    <div className={styles.detailSection}>
                        <LeasingQualifiedButton financingData={financingData} />
                    </div>
                )}
                <div className={styles.detailSection}>
                    <TrustPilot locale={locale} theme="light" variant="small" />

                    {!isMobile && (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            marginTop={2}
                        >
                            *{t(translations.serviceFeeDisclaimer)}
                        </Typography>
                    )}
                </div>
            </>
        );
    },
);

MachineOverviewSidebar.displayName = 'MachineOverSidebar';
